import { Button } from "@progress/kendo-react-buttons";
import React,{ useContext, useEffect, useState } from "react";
import "./RecordingScorecardTab.scss";
import { RecordingScorecard } from "../../types/recording/RecordingScorecard";
import ScorecardSelectDialog from "../../components/recording-scorecards/ScorecardSelectDialog";
import RecordingScorecardEvalutation from "../../components/recording-scorecards/RecordingScorecardEvaluation";
import { formatDateTime } from "../../utils/dateTimeUtils";
import { AccessPermissionEnum } from "../../enums/accessPermissionEnum";
import useAuth from "../../hooks/useAuth";
import recordingsService from "../../services/recordings.service";
import scorecardService from "../../services/scorecard.service";
import useLocale from "../../hooks/useLocale";
import { Dictionary } from "../../types/Dictionary";
import { Scorecard } from "../../types/scorecard";
import useSwal from "../../hooks/useSwal";
import { SweetAlertOptions } from "sweetalert2";
import { TinyUser } from "../../types/user";
import useTranslation from "../../hooks/useTranslation";

export interface ScorecardEvaluationQuestion {
  id: number;
  recordingScorecardDetailId?:number;
  question: string;
  category: string;
  autoQAQuestionScore:number|null;
  questionScore:number|null;
  questionEvaluatedBy:TinyUser|null;
}

export interface ScorecardEvaluationDetails {
  id?: number;
  score?: number|null;
  autoQAScore?:number|null;
  evaluatedBy?: TinyUser;
  evaluatedAt?: string;
}

export type ShortScorcardType = {
  id: number;
  text: string;
  isArchived?: boolean;
  position?: string;
};

interface RecordingScorecardTabProps {
  isCallEvaluated: boolean;
  recordingId: number;
  shared?: boolean;
  onScorecardEvaluated: () => void;
  allocatedScorecards?: Scorecard[];
  baseLicenseId: number;
  onScorecardEvalDeleted: () => void;
}

const RecordingScorecardTab: React.FC<RecordingScorecardTabProps> = ({
  isCallEvaluated,
  recordingId,
  shared,
  onScorecardEvaluated,
  allocatedScorecards,
  baseLicenseId,
  onScorecardEvalDeleted,
}) => {
  const trans = useTranslation("RecordingScorecard");
  const auth = useAuth();
  const swal = useSwal();
  const localeCtx = useLocale();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const toggleShowDialog = () => {
    setShowDialog(!showDialog);
  };
  const [selectedScorecard, setSelectedScorecard] = useState<ShortScorcardType>(
    { id: -1, text: "--Select--", isArchived: true }
  );
  const [evaluating, setEvaluating] = useState<boolean>(isCallEvaluated);
  const [evalQuestions, setEvalQuestions] = useState<
    ScorecardEvaluationQuestion[] | undefined
  >();
  const [evalDetails, setEvalDetails] = useState<
    ScorecardEvaluationDetails | undefined
  >();
  const [initialScorecard,setInitialScorecard]=useState<ShortScorcardType>({ id: -1, text: "--Select--", isArchived: true });
  const [error, setError] = useState<string | undefined>();

  const [evaluatedScorecardQuestions, setEvaluatedScorecardQuestions] =
    useState<ScorecardEvaluationQuestion[] | undefined>();

  const [edited, setEdited] = useState<boolean>(false);

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
        "RecordingScorecard"
      ]
    ) {
      trans.fetchTranslations("RecordingScorecard");
    }
  }, [localeCtx?.selectedLocale]);

  const sendFetchRecordingScorecardRequest = async () => {
    try {
      const data = await recordingsService.fetchRecordingScorecard(
        recordingId,
        shared === true
      );
      const questions: ScorecardEvaluationQuestion[] = [];
      data.scoreDetails?.forEach((d) => {
        questions.push({
          id: d.question.id!,
          recordingScorecardDetailId:d.id ?? 0,
          question: d.question.questionText,
          category: d.scorecardGroupName,
          questionScore:d.questionScore,
          autoQAQuestionScore:d.autoQAQuestionScore,
          questionEvaluatedBy:d.questionEvaluatedBy
        });
      });
      setEvalQuestions(questions);
      setEvaluatedScorecardQuestions(questions);
      setSelectedScorecard({
        id: data.scorecardName.id,
        text: data.scorecardName.name ? data.scorecardName.name : "",
        isArchived: data.scorecardName.isArchived ? true : false,
      });
      setInitialScorecard({
        id: data.scorecardName.id,
        text: data.scorecardName.name ? data.scorecardName.name : "",
        isArchived: data.scorecardName.isArchived ? true : false,
      });
      setEvaluating(true);
      setEvalDetails({
        id: data.scorecardName.id,
        score: data.score,
        evaluatedBy: data.scoredBy,
        evaluatedAt: data.scoredAt
          ? formatDateTime(data.scoredAt, "MMM DD, YYYY, HH:mm")
          : "",
        autoQAScore:data.autoQAScore
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }
    }
  };

  const sendFetchScorecardRequest = (id: number) => {
    scorecardService
      .fetchScorecardById(id, shared === true)
      .then((data) => {
        const questions: ScorecardEvaluationQuestion[] = [];
        data.questionGroups?.forEach((g) => {
          g.questions.forEach((q) =>
            questions.push({
              id: q.id!,
              question: q.questionText,
              category: g.scorecardQuestionGroupName,
              questionScore:0,
              autoQAQuestionScore:null,
              questionEvaluatedBy: auth?.user ?? null
            })
          );
        });
        setEvalQuestions(questions);
      })
      .catch((err) => {
        if (err instanceof Error) {
          setError(err.message);
        }
      });
  };

  useEffect(() => {
    if (isCallEvaluated) {
      sendFetchRecordingScorecardRequest();
    }
  }, [isCallEvaluated]);

  const selectScorecardHandler = (scorecard: ShortScorcardType) => {
    if (scorecard.id !== -1) {
      setSelectedScorecard(scorecard);
      setEvaluating(true);
      if (evalDetails && scorecard.id === evalDetails.id) {
        setEvalQuestions(evaluatedScorecardQuestions);
        setEdited(false);
      } else {
        sendFetchScorecardRequest(scorecard.id);
        setEdited(true);
      }
    } else {
      setEvaluating(false);
    }
    setShowDialog(!showDialog);
  };

  const onCloseDialog = () => {
    setShowDialog(false);
  };

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEvaluatationSave = (scorecard: RecordingScorecard) => {
    const questions: ScorecardEvaluationQuestion[] = [];
    scorecard.scoreDetails?.forEach((d) => {
      questions.push({
        id: d.question.id!,
        question: d.question.questionText,
        category: d.scorecardGroupName,
        recordingScorecardDetailId:d.id ?? 0,
        questionScore:d.questionScore,
        autoQAQuestionScore:d.autoQAQuestionScore,
        questionEvaluatedBy:d.questionEvaluatedBy
      });
    });
    setEvalQuestions(questions);
    setEvaluatedScorecardQuestions(questions);
    setEvaluating(true);
    setEvalDetails({
      id: scorecard.scorecardName.id,
      score: scorecard.score!=null ? Number(scorecard.score.toPrecision(4)) : null,
      evaluatedBy: scorecard.scoredBy,
      evaluatedAt: scorecard.scoredAt
        ? formatDateTime(scorecard.scoredAt, "MMM DD, YYYY, HH:mm")
        : "",
      autoQAScore:scorecard.autoQAScore,
    });
    onScorecardEvaluated();
  };

  const onEvaluationDelete = async () => {
    const swalOptions: SweetAlertOptions<any, any> = {
      title: trans.fetchLabelKeyTranslation(
        "SwtEvalDeleteTitle",
        "Are you sure?"
      ),
      text: trans.fetchLabelKeyTranslation(
        "SwtEvalDeleteText",
        "You won't be able to revert this!"
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: trans.fetchLabelKeyTranslation(
        "SwtEvalDeleteConfirmBtnText",
        "Yes, delete it!"
      ),
      cancelButtonText: trans.fetchLabelKeyTranslation("CancelText", "Cancel"),
    };
    const res = await swal.fire(swalOptions);
    if(res.isConfirmed){
      setEvaluating(false);
      try{
        await recordingsService.deleteRecordingScorecard(recordingId, shared);
        onScorecardEvalDeleted();
        setEvalDetails({});
        setInitialScorecard({ id: -1, text: "--Select--", isArchived: true });
        setSelectedScorecard({ id: -1, text: "--Select--", isArchived: true });
      }catch(err){
        if (err instanceof Error) {
          const swalOptions: SweetAlertOptions<any, any> = {
            title: "Scorecard cannot be deleted right now. Please try again later.",
            icon: "error",
            showCancelButton: false,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          };
          swal.fire(swalOptions);
        }
        setEvaluating(true);
      }
    }
  };

  return (
    <>
      {showDialog && (
        <ScorecardSelectDialog
          selectedScorecard={selectedScorecard}
          onCloseDialog={onCloseDialog}
          onScorecardSelect={selectScorecardHandler}
          fetchKeyTranslation={trans.fetchLabelKeyTranslation}
          allocatedScorecards={allocatedScorecards}
          baseLicenseId={baseLicenseId}
        />
      )}
      {!evaluating && (
        <div className="row">
          <div className="col-md-12">
            <div className="selectScorecard">
              <div className="selectScorecardSelect">
                <Button
                  className={`btn bg-primary line-height-1 text-white p-r-1 ${
                    !auth?.checkUserAccess(
                      AccessPermissionEnum.EvaluateCalls
                    ) || shared === true
                      ? "disabledBtn"
                      : ""
                  }`}
                  onClick={toggleShowDialog}
                  style={{ height: "40px", width: "200px" }}
                  disabled={
                    !auth?.checkUserAccess(
                      AccessPermissionEnum.EvaluateCalls
                    ) || shared === true
                  }
                >
                  {trans.fetchLabelKeyTranslation("TextEvaluate", "Evaluate")}
                  <i
                    className="bi bi-plus fs-24"
                    title={trans.fetchLabelKeyTranslation(
                      "TextEvaluate",
                      "Evaluate"
                    )}
                  ></i>
                </Button>
                <span className="text-black-9 text-center p-10">
                  {trans.fetchLabelKeyTranslation(
                    "StartEvalMsg",
                    "Please click on the Evaluate button to select the scorecard"
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {evaluating && (
        <RecordingScorecardEvalutation
          scorecard={selectedScorecard}
          onShowDialog={onShowDialog}
          isEvaluated={isCallEvaluated}
          questions={evalQuestions}
          onEvaluationSave={onEvaluatationSave}
          recordingId={recordingId}
          evaluationDetails={evalDetails}
          error={error}
          shared={shared}
          fetchKeyTranslation={trans.fetchLabelKeyTranslation}
          edited={edited}
          setEdited={setEdited}
          sendFetchRecordingScorecardRequest={
            sendFetchRecordingScorecardRequest
          }
          setEvaluating={setEvaluating}
          onEvaluationDelete={onEvaluationDelete}
          initialScorecard={initialScorecard}
        />
      )}
    </>
  );
};

export default React.memo(RecordingScorecardTab);
